import { parse } from 'uri-js';

export let PATIENT_APP_URL = '';
export let X_FORWARDED_HOST = '';
/** localhost, prismacloud.today, prismacloud.com, etc. */
export let STAGE_NAME = '';

export const heartBeatPeriod = 60 * 1000; // 1 mins in milliseconds

export const authRemainThresholdInSeconds = 5 * 60; // 5 mins in seconds

/* Version of TeamCity build for spa_PatientApp. Will be set from build.fsx when building.  */
// tslint:disable-next-line
export let BUILD_VERSION = '';

/* Version of the whole app (version of Deploy TeamCity build).
 * Will be substituted by environment variable at runtime (before running Docker image) */
export let APP_VERSION = '';

interface IAnalyticsSettings {
  AppInsights: { key: string };
  Sentry: { IsEnabled: boolean; DSN: string };
}

let _analyticsSettings: IAnalyticsSettings | null = null;
export const AnalyticsSettings = (): IAnalyticsSettings => {
  return (
    _analyticsSettings ||
    (_analyticsSettings = {
      AppInsights: {
        key: '3bfa75e1-7a0f-429a-a8bf-e0616a925b41',
      },
      Sentry: {
        DSN: envVariableOrDefault(
          'https://48eb0afc3bae488bb38e16ed7587a11c@sentry.prismacloud.cc/2',
          'https://ef0c6d9012ff4b5784c1fb1c8d20f6e5@sentry-dev.prismacloud.cc/2'
        ),
        IsEnabled: envVariableOrDefaultBool(
          '$APP_SENTRY_ENABLED',
          process.env.NODE_ENV === 'production'
        ),
      },
    })
  );
};

const DefaultStageName = 'localhost';

// Returns today/xyz/co/com when running on stages, or 'localhost' when running locally
function getStageName(input: string) {
  const regex = /.*\.prismacloud\.(.*)/g;
  const regexResult = regex.exec(input);
  if (!regexResult) {
    return DefaultStageName;
  }

  return regexResult[1];
}

function getBaseAddress(input: string) {
  const regex = /.*\.(prismacloud\..*)/g;
  const regexResult = regex.exec(input);
  if (!regexResult) {
    throw new Error(`URL ${input} is not matching regex`);
  }

  return regexResult[1];
}

export function initialize() {
  BUILD_VERSION =
    // tslint:disable-next-line
    process.env['REACT_APP_BUILD_VERSION'] || process.env.REACT_APP_BUILD_VERSION || '0.0.0.DEV';
  APP_VERSION = envVariableOrDefault(
    '$APP_DEPLOY_VERSION',
    process.env.BUILD_NUMBER || BUILD_VERSION
  );

  setUrls();
}

function setUrls() {
  STAGE_NAME = DefaultStageName;
  const serverUrl = process.env.REACT_APP_URL;

  if (serverUrl) {
    const parsedUrl = parse(serverUrl);

    STAGE_NAME = getStageName(parsedUrl.host!);
    PATIENT_APP_URL = serverUrl;
    X_FORWARDED_HOST = PATIENT_APP_URL;
  } else if (process.env.NODE_ENV === 'production') {
    const protocol = window.location.protocol;
    const baseAddress = getBaseAddress(window.location.hostname);

    STAGE_NAME = getStageName(window.location.hostname);
    PATIENT_APP_URL = `${protocol}//my.${baseAddress}`;
    X_FORWARDED_HOST = PATIENT_APP_URL;
  } else {
    PATIENT_APP_URL = 'http://localhost:8380';
    X_FORWARDED_HOST = 'http://my.patient.app';
  }

  console.log('ServerURL: ' + serverUrl);
  console.log('PAppURL: ' + PATIENT_APP_URL);
  console.log('BUILD_VERSION: ' + BUILD_VERSION);
  console.log('APP_VERSION: ' + APP_VERSION);
}

export function envVariableOrDefaultBool(envVariable: string, defaultValue: boolean) {
  const value = envVariableOrDefault(envVariable, '');
  if (value === null || value === '') {
    return defaultValue;
  }

  return convertStringToBool(value);
}

function convertStringToBool(value: string) {
  return value.toLowerCase() === 'true';
}

// Function returns value set in environment variable or defaultValue.
// Environment variables could be set:
// - at build time (TeamCity/AppCenter build configuration), app name must be REACT_APP_*.
// - at runtime (for Web app only, in env_stages repo, app name must be APP_*).
// In mobile development environment you could set env variables by editing `mob_PatientApp/.env` file.
export function envVariableOrDefault(envVariable: string, defaultValue: string): string {
  const buildEnvironmentVariableName = envVariable.replace('$APP', 'REACT_APP');
  const buildEnvironmentVariableValue = process.env[buildEnvironmentVariableName];
  if (buildEnvironmentVariableValue) {
    defaultValue = buildEnvironmentVariableValue;
  }

  if (envVariable.startsWith('$APP_')) {
    // in Web version $APP_* gets substituted by env. variables by nginx docker
    // if env variable is not set - return default
    return defaultValue;
  }
  if (envVariable === '') {
    // if env. variable is set to be empty - return default
    return defaultValue;
  }
  return envVariable;
}

export const MetricCategories = {
  ReduxEvents: 'ReduxEvents',
  Dashboard: 'Dashboard',
  Application: 'Application',
};
export const MetricActions = {
  Loaded: 'Loaded',
  DayCardTabChanged: 'DayCardTabChanged',
  DayCardsScrolled: 'DayCardsScrolled',
  FinishedWeekChangedBackward: 'FinishedWeekChangedBackward',
  FinishedWeekChangedForward: 'FinishedWeekChangedForward',
  WeekSwitched: 'WeekSwitched',
  LanguageSwitched: 'LanguageSwitched',
};

export default {
  authRemainThresholdInSeconds,
  heartBeatPeriod,
  initialize,
  MetricCategories,
  MetricActions,
  AnalyticsSettings,
  APP_VERSION,
  BUILD_VERSION,
};
